import { platforms } from "../data/platforms";
import type { FavoriteItem, GuestState, HistoryItem } from "../types";

const STORAGE_KEY = "jinri-juhe:guest:v1";
const HISTORY_LIMIT = 500;
const HISTORY_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;

const emptyState = (): GuestState => ({
  favorites: [],
  history: [],
  subscriptions: platforms.map((platform) => platform.id),
  platformOrder: platforms.map((platform) => platform.id),
});

function cleanHistory(items: HistoryItem[]): HistoryItem[] {
  const cutoff = Date.now() - HISTORY_RETENTION_MS;
  return items
    .filter((item) => Date.parse(item.viewedAt) >= cutoff)
    .sort((a, b) => Date.parse(b.viewedAt) - Date.parse(a.viewedAt))
    .slice(0, HISTORY_LIMIT);
}

function sanitize(state: Partial<GuestState>): GuestState {
  const validIds = new Set(platforms.map((platform) => platform.id));
  const requestedOrder = Array.isArray(state.platformOrder)
    ? state.platformOrder.filter((id) => validIds.has(id))
    : [];
  const missingIds = platforms.map((platform) => platform.id).filter((id) => !requestedOrder.includes(id));

  return {
    favorites: Array.isArray(state.favorites) ? state.favorites.slice(0, 1000) : [],
    history: cleanHistory(Array.isArray(state.history) ? state.history : []),
    subscriptions: Array.isArray(state.subscriptions)
      ? state.subscriptions.filter((id) => validIds.has(id))
      : platforms.map((platform) => platform.id),
    platformOrder: [...requestedOrder, ...missingIds],
  };
}

export function loadGuestState(): GuestState {
  if (typeof window === "undefined") return emptyState();

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? sanitize(JSON.parse(stored) as Partial<GuestState>) : emptyState();
  } catch {
    return emptyState();
  }
}

export function saveGuestState(state: GuestState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitize(state)));
  } catch {
    // 隐私模式或存储空间不足时仍允许继续浏览。
  }
}

export function mergeGuestStates(local: GuestState, remote: GuestState): GuestState {
  const favoriteMap = new Map<string, FavoriteItem>();
  for (const item of [...remote.favorites, ...local.favorites]) favoriteMap.set(item.key, item);

  const historyMap = new Map<string, HistoryItem>();
  for (const item of [...remote.history, ...local.history]) {
    const existing = historyMap.get(item.key);
    if (!existing || Date.parse(item.viewedAt) > Date.parse(existing.viewedAt)) {
      historyMap.set(item.key, item);
    }
  }

  return sanitize({
    favorites: [...favoriteMap.values()],
    history: [...historyMap.values()],
    subscriptions: [...new Set([...local.subscriptions, ...remote.subscriptions])],
    platformOrder: local.platformOrder.length ? local.platformOrder : remote.platformOrder,
  });
}
